import { Columns, Grid, Layout } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useChatbot } from '../../contexts/ChatbotContext';
import { useGallerySystem, type DisplayMode } from '../../hooks/useGallerySystem';

export function GalleryDisplaySettings() {
  const { chatbotPublicId } = useChatbot();
  const { settings, updateSettings, loading } = useGallerySystem();
  const [localColumns, setLocalColumns] = useState<number>(settings.gridColumns);
  const [localSpacing, setLocalSpacing] = useState<number>(settings.spacing);

  // Update local state when settings change
  useEffect(() => {
    setLocalColumns(settings.gridColumns);
    setLocalSpacing(settings.spacing);
  }, [settings.gridColumns, settings.spacing]);

  const handleDisplayModeChange = async (mode: DisplayMode) => {
    if (!chatbotPublicId) return;
    await updateSettings(chatbotPublicId, { displayMode: mode });
  };

  const handleColumnsChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault(); // Prevent form submission
    const columns = parseInt(e.target.value);
    setLocalColumns(columns);
    
    // Debounce the API call - only update after a slight delay
    const updateColumnsWithDebounce = async () => {
      if (!chatbotPublicId) return;
      try {
        await updateSettings(chatbotPublicId, { gridColumns: columns });
      } catch (err) {
        console.error('Error updating columns:', err);
      }
    };

    const timeoutId = setTimeout(updateColumnsWithDebounce, 300);
    return () => clearTimeout(timeoutId);
  }, [chatbotPublicId, updateSettings]);

  const handleSpacingChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault(); // Prevent form submission
    const spacing = parseInt(e.target.value);
    setLocalSpacing(spacing);
    
    // Debounce the API call - only update after a slight delay
    const updateSpacingWithDebounce = async () => {
      if (!chatbotPublicId) return;
      try {
        await updateSettings(chatbotPublicId, { spacing });
      } catch (err) {
        console.error('Error updating spacing:', err);
      }
    };

    const timeoutId = setTimeout(updateSpacingWithDebounce, 300);
    return () => clearTimeout(timeoutId);
  }, [chatbotPublicId, updateSettings]);

  const DisplayModeButton = ({ mode, label, icon: Icon }: {
    mode: DisplayMode;
    label: string;
    icon: typeof Grid;
  }) => (
    <button
      onClick={() => handleDisplayModeChange(mode)}
      className={`flex flex-col items-center gap-2 p-4 rounded-lg border transition-colors ${
        settings.displayMode === mode
          ? 'border-indigo-500 bg-indigo-500/20 text-indigo-400'
          : 'border-white/10 bg-white/5 text-gray-400 hover:bg-white/10'
      }`}
      disabled={loading.settings}
    >
      <Icon className="w-6 h-6" />
      <span>{label}</span>
    </button>
  );

  return (
    <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
      <h3 className="text-lg font-semibold text-white mb-6">Display configuration</h3>
      
      <div className="grid grid-cols-3 gap-4 mb-6">
        <DisplayModeButton mode="grid" label="Grille" icon={Grid} />
        <DisplayModeButton mode="masonry" label="Masonry" icon={Columns} />
        <DisplayModeButton mode="carousel" label="Carousel" icon={Layout} />
      </div>

      {settings.displayMode !== 'carousel' && (
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Number of columns
            </label>
            <input
              type="range"
              min="2"
              max="4"
              value={localColumns}
              onChange={handleColumnsChange}
              className="w-full"
              disabled={loading.settings}
            />
            <div className="flex justify-between text-sm text-gray-400 mt-2">
              <span>2</span>
              <span>3</span>
              <span>4</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Spacing
            </label>
            <input
              type="range"
              min="2"
              max="8"
              value={localSpacing}
              onChange={handleSpacingChange}
              className="w-full"
              disabled={loading.settings}
            />
            <div className="flex justify-between text-sm text-gray-400 mt-2">
              <span>Small</span>
              <span>Medium</span>
              <span>Large</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}